import { ElectionOptionID, GameID, PlayerID, SeasonID, TeamID } from "./models/common";
import { ChroniclerTeamBlessingPreferences, ElectionModel, ElectionOption, GameModel, PlayerModel, RosterLocation, SimModel, TeamModel } from "./models/models";
import { ISnapshotResults, Snapshot } from "./dataSources/snapshots";
import { All_Seasons } from "./dataSources/seasons";

export interface IPlayerCollections {
    allPlayers: PlayerModel[],
    playersById: Record<PlayerID, PlayerModel>,
    playersByTeam: Record<TeamID, Record<RosterLocation | "ANY", PlayerModel[]>>,
    //orphanedPlayers: PlayerModel[]
}

export interface ITeamCollections {
    allTeams: TeamModel[],
    teamsById: Record<TeamID, TeamModel>,
}

export interface IAllGames {
    allGames: GameModel[],
    gamesById: Record<GameID, GameModel>,
    gamesBySeason: Record<SeasonID, INormalSeasonGames>,
    currentSeasonGames: INormalSeasonGames,
}
export interface INormalSeasonGames {
    gamesByDay: GameModel[][],
    gamesByTeam: Record<TeamID, GameModel[]>
    gamesById: Record<GameID, GameModel>
}
export interface ISnapshotData {
    snapshot: Snapshot,
    simData: SimModel,
    teamCollections: ITeamCollections,
    playerCollections: IPlayerCollections,
    games: INormalSeasonGames
}

export interface IElectionData {
    topPicksByTeam: Record<TeamID, ElectionOption[]>
    topPicksByBlessing: {
        blessing: ElectionOption,
        teams: {
            team: TeamID,
            rank: number
        }[]
    }[]
}


const buildTeamCollections = (allTeams: TeamModel[]): ITeamCollections => {

    let teamsById: Record<TeamID, TeamModel> = {}
    allTeams.forEach(team => teamsById[team.id] = team);
    return {
        allTeams,
        teamsById
    }
}


interface rosterEntry {
    id: PlayerID,
    name: string,
    slotIndex: number | null
}

const buildPlayerCollections = (allTeams: TeamModel[], allPlayers: PlayerModel[]): IPlayerCollections => {
    const playersById: Record<PlayerID, PlayerModel> = {};
    const playersByTeam: Record<TeamID, Record<RosterLocation | "ANY", PlayerModel[]>> = {};
    //const orphanedPlayers: PlayerModel[] = [];

    allPlayers.forEach(player => {
        if (player === undefined) {
            console.warn("Got an undefined player!")
            return;
        }

        playersById[player.id] = player;
    })

    // Build players by team based on the team's roster data
    allTeams.forEach((team) => {
        const lineup: rosterEntry[] = []
        const rotation: rosterEntry[] = []
        const shadows: rosterEntry[] = []
        team.roster.forEach(entry => {
            entry.rosterSlots.forEach(slot => {
                if (slot.active) {
                    if (slot.location === "LINEUP") {
                        lineup.push({
                            id: entry.id,
                            name: entry.name,
                            slotIndex: slot.orderIndex
                        })
                    }
                    if (slot.location === "ROTATION") {
                        rotation.push({
                            id: entry.id,
                            name: entry.name,
                            slotIndex: slot.orderIndex
                        })
                    }
                    if (slot.location === "SHADOWS") {
                        shadows.push({
                            id: entry.id,
                            name: entry.name,
                            slotIndex: slot.orderIndex
                        })
                    }
                }
            })
        })

        const sorter = (a: rosterEntry, b: rosterEntry) => {
            if (a.slotIndex === b.slotIndex) {
                return (a.name === b.name) ? 0
                    : (a.name < b.name) ? -1 : 1; // So far, we've only seen one collision, and it seems to be sorted alphabetically. But who knows
            }
            if (b.slotIndex === null) {
                return 1
            }
            if (a.slotIndex === null) {
                return -1
            }
            return a.slotIndex - b.slotIndex
        }

        lineup.sort(sorter);
        rotation.sort(sorter);
        shadows.sort(sorter);

        // TODO does there need to be an active roster slot for a player to be part of ANY?
        const any = team.roster.map(entry => playersById[entry.id])

        playersByTeam[team.id] = {
            "LINEUP": lineup.map(entry => playersById[entry.id]),
            "ROTATION": rotation.map(entry => playersById[entry.id]),
            "SHADOWS": shadows.map(entry => playersById[entry.id]),
            "ANY": any,
        }
    })

    // Old code to build players By Team based on player data
    /*
    allPlayers.forEach(player => {

        if (player === undefined) {
            console.warn("Got an undefined player!")
            return;
        }

        playersById[player.id] = player;

        // Load playersByTeam from team roster data instead
        
        if (player.team === undefined) {
            if (REPORT_ALL_INVALID_PLAYERS) {
                console.log(`Player ${player.name} has no team`)
            }
            return;
        }
        if (!playersByTeam[player.team.id]) {
            if (REPORT_ALL_INVALID_PLAYERS) {

                console.log(`Player ${player.name} is on an invalid team (${player.team.name})`)
            }
        } else {

            
            const team = allTeams.filter(team => team.id === player.team.id)[0];

            
            if (!team.roster.map(entry => entry.id).includes(player.id)) {
                if (REPORT_ALL_INVALID_PLAYERS) {

                    console.log(`Player ${player.name} clings to team ${player.team.name} but the team doesn't claim them`)
                }
                orphanedPlayers.push(player);
            } else {

                const teamPlayers = playersByTeam[team.id];
                player.rosterSlots.forEach(slot => {


                    if (slot.orderIndex !== null) {
                        if (teamPlayers[slot.location][slot.orderIndex]) {
                            if (REPORT_SOME_INVALID_PLAYERS) {
                                console.log(`${team.name} ${slot.location}[${slot.orderIndex}] occupied by ${teamPlayers[slot.location][slot.orderIndex].name} and ${player.name}`)
                            }
                        } else {
                            teamPlayers[slot.location][slot.orderIndex] = (player);
                        }
                    } else {
                        teamPlayers[slot.location].push(player);
                    }

                })
                playersByTeam[player.team.id].ANY.push(player)
            }
            

        }


        if (player.attributes.length !== 15 && REPORT_SOME_INVALID_PLAYERS) {
            console.log(`Player ${player.name} (${player.team.name}) has ${player.attributes.length} attributes.`)
        }
        

    })

    // Fix players who have the wrong team marked in their model
    allTeams.forEach((team) => {
        team.roster.forEach(player => {
            player.rosterSlots.forEach(slot => {
                if (slot.orderIndex) {
                    if (!playersByTeam[team.id][slot.location].map(x => x.id).includes(player.id)) {

                        if (REPORT_SOME_INVALID_PLAYERS) {

                            console.log(`  Fixing ${player.name} (${team.name})`)
                        }
                        playersByTeam[team.id][slot.location].push(playersById[player.id]);

                        const anyList = playersByTeam[team.id].ANY
                        if (anyList.filter(anyPlayer => anyPlayer.id === player.id).length === 0) {
                            anyList.push(playersById[player.id])
                        }
                    }
                }
            })
        })
    })
    //*/

    return {
        allPlayers,
        playersById,
        playersByTeam,
        //orphanedPlayers
    }
}


const buildGames = (allTeams: TeamModel[], allGames: GameModel[], simData: SimModel): IAllGames => {

    const gamesBySeason: Record<SeasonID, INormalSeasonGames> = {}
    All_Seasons.forEach(season => {
        const seasonId = season.id;

        const gamesToUse = allGames.filter(game => game.seasonId === seasonId)

        // Old debug code to check that we got all the games
        /*
        const daysSeen: number[] = []
        const gamesSeenByDay: number[] = []
        const currentDay = simData.simData.currentDay;
        gamesToUse.forEach(game => {
            if (!daysSeen.includes(game.day)) {
                daysSeen.push(game.day)
                gamesSeenByDay[game.day] = 0;
            }
            gamesSeenByDay[game.day]++;
        })
        console.log(`Checking season ${simData.simData.currentSeasonNumber} : ${simData.simData.currentDay}`)
        for (var i = 0; i < currentDay; i++) {
            console.log(`    Day ${i}: ${daysSeen.includes(i)} [${gamesSeenByDay[i]}]`)
        }
        //*/


        const gamesByDay: GameModel[][] = []
        const gamesByTeam: Record<TeamID, GameModel[]> = {}
        // Setup
        for (let team of allTeams) {
            gamesByTeam[team.id] = []
        }
        // Populate with games
        for (let game of gamesToUse) {
            if (!gamesByDay[game.day]) {
                gamesByDay[game.day] = []
            }
            gamesByDay[game.day].push(game)
            gamesByTeam[game.homeTeam.id][game.day] = game;
            gamesByTeam[game.awayTeam.id][game.day] = game;
        }

        const gamesById: Record<GameID, GameModel> = {}

        gamesToUse.forEach(game => {
            gamesById[game.id] = game;
        })

        const data = { gamesByDay, gamesByTeam, gamesById };

        gamesBySeason[seasonId] = data;

    })

    const currentSeasonGames = gamesBySeason[simData.simData.currentSeasonId]


    const gamesById: Record<GameID, GameModel> = {}
    allGames.forEach(game => {
        gamesById[game.id] = game;
    })

    return {
        currentSeasonGames,
        gamesBySeason,
        allGames,
        gamesById
    }
}

const buildSnapshotsData = (snapshotInfos: ISnapshotResults[], allTeams: TeamModel[], allGames: IAllGames): ISnapshotData[] => {

    const snapshots: ISnapshotData[] = [];

    snapshotInfos.forEach((result) => {
        const { teamsResult, playersResult, simResult } = result;

        const currentTeamIDs = allTeams.map(team => team.id);
        const teams = teamsResult.items.map(item => item.data).filter(team => currentTeamIDs.includes(team.id));
        const playerList = teams.map(team => team.roster).reduce((a, b) => a.concat(b), []).map(entry => entry.id)
        const players = playersResult.items.map(item => item.data).filter(player => playerList.includes(player.id))


        const teamCollections: ITeamCollections = Processors.buildTeamCollections(teams)
        const playerCollections: IPlayerCollections = Processors.buildPlayerCollections(teams, players);


        if (simResult.items.length !== 1) {
            console.warn(`Error loading snapshot ${result.snapshot.name} - found ${simResult.items.length} sim data objects`)
        }
        const simData = simResult.items[0].data;

        const seasonId = simData.simData.currentSeasonId
        const games = allGames.gamesBySeason[seasonId]

        snapshots.push({
            snapshot: result.snapshot,
            playerCollections,
            teamCollections,
            games,
            simData
        })

    })

    return snapshots;
}

const buildElectionData = (allTeams: TeamModel[], elections: ElectionModel, preferences: ChroniclerTeamBlessingPreferences[]): IElectionData => {

    const topPicksByTeam: Record<TeamID, ElectionOption[]> = {};

    allTeams.forEach(team => {
        topPicksByTeam[team.id] = []
    })

    const blessings: ElectionOptionID[] = [];

    const electionsById: Record<ElectionOptionID, ElectionOption> = {};
    elections.blessings.forEach(group => {
        group.electionOptions.forEach(option => {
            electionsById[option.id] = option;
        })
    })

    const listByBlessing: Record<ElectionOptionID, { team: TeamID, rank: number }[]> = {}
    preferences.forEach(preference => {
        preference.groups.forEach((group) => {

            group.top_option_ids.forEach((optionId, idx) => {
                if (listByBlessing[optionId] === undefined) {
                    listByBlessing[optionId] = []
                }
                if (!blessings.includes(optionId)) {
                    blessings.push(optionId)
                }
                listByBlessing[optionId].push({
                    team: preference.team_id,
                    rank: idx + 1
                })
            })

            topPicksByTeam[preference.team_id] = group.top_option_ids.map(optionId => electionsById[optionId])
        })
    })

    const topPicksByBlessing = blessings.map(optionId => {
        return {
            blessing: electionsById[optionId],
            teams: listByBlessing[optionId]
        }
    })


    return {
        topPicksByTeam,
        topPicksByBlessing
    }
}

export const Processors = {
    buildTeamCollections,
    buildPlayerCollections,
    buildGames,
    buildSnapshotsData,
    buildElectionData
}